import React from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl, Grid,
    Grow,
    IconButton,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField
} from "@mui/material";
import {visuallyHidden} from '@mui/utils';
import PropTypes from 'prop-types';
import AttendanceData from "../../backend/dataservices/AttendanceData";
import CourseData from "../../backend/dataservices/CourseData";
import {useLocation, useNavigate} from "react-router-dom";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import User from "../../backend/User";
import InputLabel from "@mui/material/InputLabel";
import TableUtils from "../../utils/TableUtils";
import TableDateChooser from "../TableDateChooser";
import AddIcon from "@mui/icons-material/Add";
import CreateNewAttendanceDialogComponent from "../CreateNewAttendanceDialogComponent";
import CoursesFilterAndReports from "../CoursesFilterAndReports";
import moment from "moment";

function createData(employeeName, date, duration, status, notes, last_change, employeeID, type, attendanceID, courseName, typ) {
    return {employeeName, date, duration, status, notes, last_change, employeeID, type, attendanceID, courseName, typ};
}

function createSuggestionsData(_name, _course) {
    return {name: _name, course: _course};
}

function descendingComparator(a, b, orderBy) {
    if (orderBy === 'date') {
        let dateA;
        let dateB;
        if(a[orderBy].length >= 11){
            dateA = moment(a[orderBy].substring(0, 10), "DD.MM.YYYY").toDate();
        } else {
            dateA = moment(a[orderBy], "YYYY").toDate();
        }
        if(b[orderBy].length >= 11){
            dateB = moment(b[orderBy].substring(0, 10), "DD.MM.YYYY").toDate();
        } else {
            dateB = moment(b[orderBy], "YYYY").toDate();
        }

        if (dateB < dateA) {
            return -1;
        } else if (dateB > dateA) {
            return 1;
        } else {
            return 0;
        }
    }
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;

}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

const headCells = [
    {
        id: 'employeeName',
        numeric: false,
        disablePadding: false,
        label: 'Mitarbeiter',
    },
    {
        id: 'date',
        numeric: false,
        disablePadding: false,
        label: 'Termin',
    },
    {
        id: 'duration',
        numeric: false,
        disablePadding: false,
        label: 'Dauer',
    },
    {
        id: 'status',
        numeric: false,
        disablePadding: false,
        label: 'Status',
    },
    {
        id: 'notes',
        numeric: false,
        disablePadding: false,
        label: 'Bemerkungen',
    },
    {
        id: 'typ',
        numeric: false,
        disablePadding: false,
        label: 'Typ',
    },
    {
        id: 'last_change',
        numeric: false,
        disablePadding: false,
        label: 'Letzte Bearbeitung',
    },
    {
        id: 'delete',
        numeric: false,
        disablePadding: false,
        label: '',
    },
]

function EnhancedTableHead(props) {
    const {order, orderBy, onRequestSort} =
        props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };
    return (
        <TableHead>
            <TableRow>
                {headCells.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align={headCell.numeric ? 'right' : 'left'}
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    onRequestSort: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

export default function CoursesTable() {


    const [suggestions, setSuggestions] = React.useState([]);
    const [openSuggestion, setOpenSuggestion] = React.useState(false);
    const [suggestionValue, setSuggestionValue] = React.useState(null);
    const [order, setOrder] = React.useState('desc');
    const [orderBy, setOrderBy] = React.useState('date');
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [rows, setRows] = React.useState([]);
    const [originalRows, setOriginalRows] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [inputValue, setInputValue] = React.useState("");
    const [openDialog, setOpenDialog] = React.useState(false);
    const [showAttendanceDeleteDialog, setShowAttendanceDeleteDialog] = React.useState(false)
    const [attendanceDeleteDialogData, setAttendanceDeleteDialogData] = React.useState({})
    const [snackbarOpen, setSnackbarOpen] = React.useState(false);
    const [snackbarText, setSnackbarText] = React.useState("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState("success");
    const [openDateChooserDialog, setOpenDateChooserDialog] = React.useState(false);
    const [dateChooserDialogData, setDateChooserDialogData] = React.useState({});
    const [stateSelectorValue, setStateSelectorValue] = React.useState(new Map());
    const [typeSelectorValue, setTypeSelectorValue] = React.useState(new Map());
    const [textfieldValue, setTextfieldValue] = React.useState(new Map());
    const [availableDates, setAvailableDates] = React.useState([]);
    const [dateSelectorValue, setDateSelectorValue] = React.useState(new Map());
    const [filterVariables, setFilterVariables] = React.useState({});
    const [filterActive, setFilterActive] = React.useState(false);
    const [showFilter, setShowFilter] = React.useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    React.useEffect(() => {
        (async () => {
            if (location.state != null && location.state.name != null && location.state.name.length !== 0) {
                sessionStorage.setItem('course', location.state.id);
                CourseData.fetchCourseBaseData(location.state.id).then(value => {
                    setSuggestionValue(createSuggestionsData(value.KS_Kursname, value))
                    fetchAttendanceData(value);
                    setShowFilter(true);
                    setFilterVariables({});
                    setFilterActive(false);
                });
            } else {
                if(sessionStorage.getItem('course')) {
                    CourseData.fetchCourseBaseData(sessionStorage.getItem('course')).then(value => {
                        setSuggestionValue(createSuggestionsData(value.KS_Kursname, value))
                        fetchAttendanceData(value);
                        setShowFilter(true);
                        setFilterVariables({});
                        setFilterActive(false);
                    });
                }
            }
        })();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    async function searchCourse(name) {
        setLoading(true);
        return CourseData.searchCourse(name).then(generateSuggestions);
    }

    async function loadDataFromDatabase(rawCourseData) {
        let tempRows = [];
        for (const course of rawCourseData) {
            tempRows.push(createSuggestionsData(course.KS_Kursname, course));
        }
        return tempRows;
    }


    function generateSuggestions(rawCourseData) {
        loadDataFromDatabase(rawCourseData).then(data => {
            setSuggestions(data);
            setLoading(false);
        });
    }

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    function handleInputValue(value) {
        if(value != null) {
            let course = value.course;
            if (course != null) {
                fetchAttendanceData(course);
            }
        } else {
            setRows([]);
            setOriginalRows([]);
        }
    }

    function fetchAttendanceData(course) {
        let tempRows = [];
        CourseData.fetchDates(course).then(dates => {
            // Die Reihenfolge kommt sortiert vom Server (neueste zuerst) und darf
            // hier nicht umgedreht werden -- das Dropdown zeigt nur ~4 Eintraege.
            setAvailableDates(dates);
        }).then(value => {
            AttendanceData.fetchAllAttendancesForCourse(course['KS_ID'])
                .then(atts => {
                    if (!Array.isArray(atts)) { setRows([]); return; }
                    atts.forEach(function (value, key) {
                        let attendanceID = value._type === 'plannedAttendance' ? value.KA_ID : value.KV_ID;
                        tempRows.push(createData(
                            value.M_Name + ', ' + value.M_Vorname,
                            TableUtils.dateFormatter(value),
                            TableUtils.durationFormatter(value),
                            value._type === 'attendance' ? capitalizeFirstLetter(value.KV_Statustext) : capitalizeFirstLetter(value.KA_Status),
                            commentValueGetter(value),
                            value._type === 'attendance' ? value.KV_Bearbeiter : value.KA_Bearbeiter,
                            value['M_Pers-Nr'],
                            value._type,
                            attendanceID,
                            course.KS_Kursname,
                            value._type === 'plannedAttendance' ? value.KA_Typ : value.KV_Typ
                        ));
                        setStateSelectorValue(new Map(stateSelectorValue.set(attendanceID, value._type === 'attendance' ? capitalizeFirstLetter(value.KV_Statustext) : capitalizeFirstLetter(value.KA_Status))))
                        setTypeSelectorValue(new Map(typeSelectorValue.set(attendanceID, value._type === 'plannedAttendance' ? value.KA_Typ : value.KV_Typ)))
                        setTextfieldValue(new Map(textfieldValue.set(attendanceID, commentValueGetter(value))));
                        setDateSelectorValue(new Map(dateSelectorValue.set(attendanceID, value.KD_ID)));
                    });
                }).then(value1 => {
                    setRows(tempRows);
                    setOriginalRows(tempRows);
                    if(filterActive) {
                        applyFilters(filterVariables.year, filterVariables.date, filterVariables.state, tempRows)
                    }
            })
        });
    }

    function capitalizeFirstLetter(string) {
        return string.charAt(0).toUpperCase() + string.slice(1);
    }

    function commentValueGetter(_value) {
        let value =
            _value._type === 'attendance'
                ? _value.KV_Bemerkungen
                : _value.KA_Bemerkungen
        if (value == null) {
            return ""
        } else {
            return value
        }
    }

    function toEmployees(employee, employeeID) {
        navigate("/employees", {state: {id: employeeID, name: employee}});
    }

    function closeSnackbar() {
        setSnackbarOpen(false);
    }

    function deleteCourse() {
        AttendanceData.deletePlannedAttendance(attendanceDeleteDialogData.attendanceID).then(value => {
            handleInputValue(suggestionValue);
            setSnackbarSeverity("success");
            setSnackbarText('Kurszuweisung erfolgreich gelöscht!')
            setSnackbarOpen(true);
        }, (error) => {
            setSnackbarSeverity("error");
            setSnackbarText('Fehler beim Löschen der Kurszuweisung!');
            setSnackbarOpen(true);
        }).then(value => {
            setShowAttendanceDeleteDialog(false);
            setAttendanceDeleteDialogData({});
        })
    }

    function handleAttendanceDialogClose() {
        setShowAttendanceDeleteDialog(false);
        setAttendanceDeleteDialogData({});
    }

    function handleNoteChange(attendanceID, type) {
        AttendanceData.saveAttendanceComment(attendanceID, textfieldValue.has(attendanceID) ? textfieldValue.get(attendanceID) : '', type).then(value => {
            setSnackbarSeverity("success");
            setSnackbarText('Bemerkung gespeichert');
            setSnackbarOpen(true);
            handleInputValue(suggestionValue)
        }, (error) => {
            setSnackbarSeverity("error");
            setSnackbarText('Fehler beim Schreiben der Bemerkung');
            setSnackbarOpen(true);
        });
    }

    function handleNoteChangeEnter(event, attendanceID, type) {
        if (event.key === 'Enter') {
            event.target.blur();
            handleNoteChange(attendanceID, type);
        }
    }

    function closeDateChooserDialog() {
        setOpenDateChooserDialog(false);
        handleInputValue(suggestionValue);
        setSnackbarSeverity("success");
        setSnackbarText("Status erfolgreich geändert!")
        setSnackbarOpen(true)
    }

    function abortChanges(employeeID) {
        setOpenDateChooserDialog(false);
        setSnackbarSeverity("error");
        setSnackbarText("Ein unerwarteter Fehler ist aufgetreten")
        setSnackbarOpen(true)
        let newMap = stateSelectorValue;
        newMap.set(employeeID, dateChooserDialogData.oldValue);
        setStateSelectorValue(newMap);
    }

    function handleCloseDialog() {
        setOpenDialog(false);
    }

    async function handleDateChange(attendanceID, newDateID) {
        AttendanceData.changeAttendanceDate(attendanceID, newDateID).then(value => {
            setTypeSelectorValue(new Map(typeSelectorValue.set(attendanceID, value)));
            setSnackbarSeverity("success");
            setSnackbarText("Termin erfolgreich geändert!");
            setSnackbarOpen(true);
        }, (error) => {
            setSnackbarSeverity("error");
            setSnackbarText("Ein unerwarteter Fehler ist aufgetreten");
            setSnackbarOpen(true);
        }).then(() => {
            handleInputValue(suggestionValue);
        });
    }

    async function handleTypChange(attendanceID, type, newValue) {
        if(type === 'plannedAttendance'){
            AttendanceData.updatePlannedTypeAttendance(attendanceID, newValue).then(value => {
                setTypeSelectorValue(new Map(typeSelectorValue.set(attendanceID, newValue)));
                setSnackbarSeverity("success");
                setSnackbarText("Typ erfolgreich geändert!");
                setSnackbarOpen(true);
            }, (error) => {
                setSnackbarSeverity("error");
                setSnackbarText("Ein unerwarteter Fehler ist aufgetreten");
                setSnackbarOpen(true);
            }).then(() => {
                handleInputValue(suggestionValue);
            });
        } else {
            AttendanceData.changeAttendanceType(attendanceID, newValue).then(value => {
                setTypeSelectorValue(new Map(typeSelectorValue.set(attendanceID, newValue)));
                setSnackbarSeverity("success");
                setSnackbarText("Typ erfolgreich geändert!");
                setSnackbarOpen(true);
            }, (error) => {
                setSnackbarSeverity("error");
                setSnackbarText("Ein unerwarteter Fehler ist aufgetreten");
                setSnackbarOpen(true);
            }).then(() => {
                handleInputValue(suggestionValue);
            });
        }
    }

    function createMenuItems() {
            if(availableDates.length > 0) {
                return availableDates.map(function (value) {
                    if(value != null) {
                        value._type = 'attendance';
                        return <MenuItem key={value.KD_ID} value={value.KD_ID}>{TableUtils.dateFormatter(value)}</MenuItem>
                    }
                    return null;
                });
            }
        return null;
    }

    function applyFilters(year, date, state, newRows = null) {
        setFilterVariables({"year": year, "date": date, "state": state});
        if(year == null && date.name === '' && state === '') {
            setRows(newRows == null ? originalRows : newRows);
            setFilterActive(false);
            return;
        }
        setFilterActive(true);
        let filteredRows = newRows == null ? originalRows : newRows;
        if(year != null) {
            filteredRows = filteredRows.filter(row => row.date.includes(year));
        }
        if(date.name !== '') {
            filteredRows = filteredRows.filter(row => row.date === date.name);
        }
        if(state !== '') {
            filteredRows = filteredRows.filter(row => stateSelectorValue.get(row.attendanceID) === state);
        }
        setRows(filteredRows);
    }

    return (
        <Box sx={{width: '98%'}} style={{
            position: 'absolute', left: '50%',
            transform: 'translate(-50%)'
        }}>
            <Paper sx={{width: '100%', mb: 1, marginTop: '1%'}}>
                <Grid container spacing={2} alignItems={'center'} justifyContent={'center'}>
                    <Grid item xs={4} justifyContent={'center'} alignItems={'center'} marginTop={'1%'} marginBottom={'1%'} marginLeft={0.1}>
                    <Autocomplete options={suggestions}
                                  fullWidth
                              open={openSuggestion}
                              onOpen={() => {
                                  setOpenSuggestion(true)
                              }}
                              onClose={() => {
                                  setOpenSuggestion(false)
                              }}
                              inputValue={inputValue}
                              onInputChange={(event, newInputValue) => {
                                  setInputValue(newInputValue);
                                  if(suggestionValue == null) {
                                      setShowFilter(false);
                                      setFilterActive(false);
                                      setFilterVariables({});
                                  }
                                  if(newInputValue.length >= 2) {
                                      searchCourse(newInputValue);
                                  } else {
                                      setShowFilter(false);
                                      setFilterActive(false);
                                      setFilterVariables({});
                                  }
                              }}

                              value={suggestionValue}
                              onChange={(event, newInputValue) => {
                                  setSuggestionValue(newInputValue);
                                  setFilterActive(false);
                                  setFilterVariables({});
                                  setShowFilter(true);
                                  if(newInputValue != null) {
                                      sessionStorage.setItem("course", newInputValue.course.KS_ID);
                                  }
                                  handleInputValue(newInputValue);
                              }}
                              isOptionEqualToValue={(option, value) => option.name.includes(value.name)}
                              getOptionLabel={(option) => option.name}
                              renderInput={(params => (
                                  <TextField
                                      {...params}
                                      variant={"outlined"}
                                      label={"Suche..."}
                                      sx={{width: '100%'}}
                                      InputProps={{
                                          ...params.InputProps,
                                          endAdornment: (
                                              <React.Fragment>
                                                  {loading ? <CircularProgress color={"primary"}/> : null}
                                                  {params.InputProps.endAdornment}
                                              </React.Fragment>
                                          ),
                                      }}
                                  />
                              ))}/>
                    </Grid>
                    <Grid item xs={5}>
                        {suggestionValue != null && showFilter ?
                            <CoursesFilterAndReports course={suggestionValue.course} applyFilters={(year, date, state) => {
                                applyFilters(year, date, state)}
                            }/>
                            :
                            null
                        }
                    </Grid>
                    <Grid item xs={2}>
                    {suggestionValue != null ?
                        <Box display={'flex'} justifyContent={'flex-end'}>
                            <Button variant={"contained"} endIcon={<AddIcon/>} onClick={() => {
                                setOpenDialog(true);
                            }} sx={{alignSelf: 'flex-end'}}>
                                Neue Kursanmeldung
                            </Button>
                        </Box>
                        :
                        null}
                    </Grid>
                </Grid>

                <CreateNewAttendanceDialogComponent
                    openDialog={openDialog}
                    handleClose={handleCloseDialog}
                    handleSuccess={() => {
                        setOpenDialog(false);
                        setSnackbarText("Neue Kursanmeldung für " + suggestionValue.name + " erfolgreich!");
                        setSnackbarSeverity("success");
                        setSnackbarOpen(true);
                        handleInputValue(suggestionValue);
                    }}
                    handleError={() => {
                        setOpenDialog(false);
                        setSnackbarText("Bei der Kursanmeldung ist ein unerwarteter Fehler aufgetreten")
                        setSnackbarSeverity("error");
                        setSnackbarOpen(true);
                    }}
                    searchCourses={false}
                    courseID={suggestionValue != null ? suggestionValue.course.KS_ID : ''}/>


                <TableContainer>
                    <Table
                        sx={{minWidth: 750}}
                        width={'80%'}
                        aria-labelledby="tableTitle"
                        size={'medium'}
                    >
                        <EnhancedTableHead
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            rowCount={rows.length}
                        />
                        <TableBody>
                            {/* if you don't need to support IE11, you can replace the `stableSort` call with:
                 suggestions.slice().sort(getComparator(order, orderBy)) */}
                            {stableSort(rows, getComparator(order, orderBy))
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row, index) => {
                                    const labelId = `enhanced-table-checkbox-${index}`;
                                    return (
                                        <TableRow
                                            hover
                                            tabIndex={-1}
                                            key={row.attendanceID}
                                        >
                                            <TableCell
                                                component="th"
                                                id={labelId}
                                                scope="row"
                                                padding="none"
                                                style={{cursor: "pointer"}}
                                                onClick={() => toEmployees(row.employeeName, row.employeeID)}
                                            >
                                                {row.employeeName}
                                            </TableCell>
                                            <TableCell align="left">
                                                {(User.getRole() === 'Verwalter' && row.type === 'attendance' && dateSelectorValue.get(row.attendanceID) != null) ?
                                                <FormControl size={'small'}>
                                                    <InputLabel>Termin</InputLabel>
                                                    <Select
                                                        value={dateSelectorValue.get(row.attendanceID)}
                                                        label={"Termin"}
                                                        MenuProps={{style: {maxHeight: 200}}}
                                                        onChange={(event) => {
                                                            handleDateChange(row.attendanceID, event.target.value);
                                                            }
                                                        }
                                                    >
                                                        {
                                                            createMenuItems(row)
                                                        }
                                                    </Select>
                                                </FormControl>
                                                :
                                                row.date}
                                            </TableCell>
                                            <TableCell align="left">{row.duration}</TableCell>
                                            <TableCell align="left">
                                                {User.getRole() === 'Verwalter' ?
                                                    <FormControl fullWidth size={"small"}>
                                                        <InputLabel>Status</InputLabel>
                                                        <Select
                                                            autoWidth
                                                            value={stateSelectorValue.get(row.attendanceID) == null ? row.status : stateSelectorValue.get(row.attendanceID)}
                                                            label={"Status"}
                                                            onChange={(event) => {
                                                                setDateChooserDialogData({
                                                                    oldValue: row.status,
                                                                    newValue: event.target.value,
                                                                    employeeName: row.employeeName,
                                                                    course: suggestionValue.course,
                                                                    employeeID: row.employeeID,
                                                                    attendanceID: row.attendanceID,
                                                                    type: typeSelectorValue.get(row.attendanceID)
                                                                });
                                                                setStateSelectorValue(new Map(stateSelectorValue.set(row.attendanceID, event.target.value)));

                                                                let openDateChooser = TableUtils.handleStatusChangerChange(event, row);
                                                                if (openDateChooser)
                                                                    setOpenDateChooserDialog(openDateChooser);
                                                                else {
                                                                    TableUtils.changeState(event, row, event.target.value).then((value) => {
                                                                        setSnackbarSeverity("success");
                                                                        setSnackbarText("Status erfolgreich geändert!");
                                                                        setSnackbarOpen(true);
                                                                    }, (error) => {
                                                                        setSnackbarSeverity("error");
                                                                        setSnackbarText("Ein unerwarteter Fehler ist aufgetreten");
                                                                        setSnackbarOpen(false);
                                                                    });
                                                                }
                                                            }}
                                                            defaultValue={row.status}
                                                        >
                                                            {TableUtils.states.map(function (value) {
                                                                return <MenuItem
                                                                    key={value.name} value={value.name}>{value.name}</MenuItem>
                                                            })}
                                                        </Select>
                                                    </FormControl>
                                                :
                                                    row.status}
                                            </TableCell>
                                            <TableCell align="left">
                                                <TextField
                                                    variant={"standard"}
                                                    value={textfieldValue.get(row.attendanceID)}
                                                    onChange={(event) => {
                                                        setTextfieldValue(new Map(textfieldValue.set(row.attendanceID, event.target.value)))
                                                    }}
                                                    onBlur={(e) => {
                                                        handleNoteChange(row.attendanceID, row.type);
                                                    }}
                                                    fullWidth
                                                    InputProps={{disableUnderline: true}}
                                                    onKeyDown={(e) => {
                                                        handleNoteChangeEnter(e, row.attendanceID, row.type)

                                                    }}/>
                                            </TableCell>
                                            <TableCell align="left" style={{width: '13%'}}>
                                                {User.getRole() === 'Verwalter' ?
                                                    <FormControl fullWidth size={"small"}>
                                                        <InputLabel>Typ</InputLabel>
                                                        <Select
                                                            required
                                                            autoWidth
                                                            displayEmpty
                                                            label="Typ"
                                                            value={typeSelectorValue.get(row.attendanceID) == null ? '' : typeSelectorValue.get(row.attendanceID)}
                                                            onChange={(event) => {
                                                                handleTypChange(row.attendanceID, row.type, event.target.value);
                                                            }}
                                                            MenuProps={{style: {maxHeight: 200}}}>
                                                            {TableUtils.getTypes().map(function (typ) {
                                                                return <MenuItem key={typ} value={typ}>{typ}</MenuItem>
                                                            })
                                                            }
                                                        </Select>
                                                    </FormControl>
                                                    :
                                                    typeSelectorValue.get(row.attendanceID) == null ? '' : typeSelectorValue.get(row.attendanceID)}
                                            </TableCell>
                                            <TableCell align="left">{row.last_change}</TableCell>
                                            <TableCell align="left">
                                                {(row.type === "plannedAttendance" && row.status === 'Geplant') ? (
                                                        <IconButton size={"large"} color={"error"} onClick={() => {
                                                            setAttendanceDeleteDialogData({
                                                                name: suggestionValue.name,
                                                                courseName: row.courseName,
                                                                courseID: row.courseID,
                                                                attendanceID: row.attendanceID
                                                            });
                                                            setShowAttendanceDeleteDialog(true);
                                                        }}>
                                                            <DeleteForeverIcon/>
                                                        </IconButton>
                                                    )
                                                    :
                                                    null
                                                }
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 20]}
                    component="div"
                    count={rows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            <Dialog open={showAttendanceDeleteDialog} onClose={handleAttendanceDialogClose}>
                <DialogTitle>
                    Geplanten Kurs löschen
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Möchten Sie den
                        Kurs {attendanceDeleteDialogData.courseName} für {attendanceDeleteDialogData.name} wirklich
                        löschen?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleAttendanceDialogClose} variant={"text"}
                            style={{color: "gray"}}>Abbrechen</Button>
                    <Button color={"error"} variant={"text"} onClick={deleteCourse}>Löschen</Button>
                </DialogActions>
            </Dialog>

            {openDateChooserDialog ?
                <TableDateChooser
                    openDialog={openDateChooserDialog}
                    onClose={closeDateChooserDialog}
                    attendanceID={dateChooserDialogData.attendanceID}
                    employeeName={dateChooserDialogData.employeeName}
                    employeeID={dateChooserDialogData.employeeID}
                    course={dateChooserDialogData.course}
                    newValue={dateChooserDialogData.newValue}
                    type={dateChooserDialogData.type}
                    abortChanges={() => abortChanges(dateChooserDialogData.employeeID)}/>
                :
                null
            }

            <Snackbar open={snackbarOpen} autoHideDuration={5000} onClose={closeSnackbar}
                      anchorOrigin={{vertical: "top", horizontal: "right"}} TransitionComponent={Grow}>
                <Alert onClose={closeSnackbar} severity={snackbarSeverity} sx={{width: '100%'}}>
                    {snackbarText}
                </Alert>
            </Snackbar>

        </Box>
    );
}

